import React, {useMemo} from 'react';
import {Block, Check, ManageAccounts} from '@mui/icons-material';
import {Button, TableCell, Typography} from '@mui/material';
import {useQuery} from '@apollo/client/react';
import {FormContainer, SubmitHandler} from 'react-hook-form-mui';
import {toast} from 'react-toastify';

import {ResourceContextProvider, useResourceContext} from 'components/record/ResourceContext';
import {BooleanField, ListField, UserStatusField} from 'components/list/Fields';
import List from 'components/list/List';
import ActionMenu from 'components/list/actionMenu/ActionMenu';
import ActionMenuItemUpdate from 'components/list/actionMenu/ActionMenuItemUpdate';
import ActionMenuItemConfirmDialog from 'components/list/actionMenu/ActionMenuItemConfirmDialog';
import {ActionMenuItemProps} from 'components/list/actionMenu/ActionMenuItem';
import {useRecordContext} from 'components/record/useRecordContext';
import {useDialogContext} from 'components/dialog/DialogContext';
import {useMutate} from 'hooks/useFetchData';
import {AutocompleteInput} from 'components/forms/Inputs';
import TotalChip from 'components/list/TotalChip';
import {RecordType} from 'components/record/RecordContext';
import {getRoles} from 'services/gqlQueries';
import AllUsersFilters from './AllUsersFilters';
import RecentActivitiesMenuItem from './RecentActivitiesMenuItem';

const MEMBER_ROLE_NAME = 'member';
const CHANGE_ROLES_FORM_ID = 'change_user_roles';

type RoleItem = {
  id: number;
  name: string;
};

type RoleOption = {
  id: number;
  label: string;
  name: string;
};

type ChangeRolesFormValues = {
  roleIds: number[];
};

const formatRoleLabel = (name: string) =>
  name
    .split(' ')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const uniqueIds = (ids: number[]) => [...new Set(ids)];

const UserGroupsField = (_props: {source: string; label: string}) => {
  const {record} = useRecordContext();
  const groups = (record?.userGroups || []) as Array<{id: number; name: string}>;
  const label = groups.length ? groups.map((group) => group.name).join(', ') : '—';
  return <TableCell>{label}</TableCell>;
};

const UserRolesField = (_props: {source: string; label: string}) => {
  const {record} = useRecordContext();
  const roles = (record?.roles || []) as Array<{id: number; name: string}>;
  const label = roles.length ? roles.map((role) => formatRoleLabel(role.name)).join(', ') : '—';
  return <TableCell>{label}</TableCell>;
};

const ChangeRolesForm = ({roles, onSuccess}: {roles: RoleItem[]; onSuccess?: () => void}) => {
  const {id, record} = useRecordContext();
  const {params} = useResourceContext();
  const {close} = useDialogContext();
  const [mutate] = useMutate<{item: RecordType}>('UPDATE_ONE', 'AllUser', {variables: params});

  const options = useMemo<RoleOption[]>(
    () => roles.map((role) => ({id: role.id, label: formatRoleLabel(role.name), name: role.name})),
    [roles]
  );
  const memberRoleId = roles.find((role) => role.name === MEMBER_ROLE_NAME)?.id;
  const defaultRoleIds = useMemo(() => {
    const currentIds = ((record?.roles || []) as RoleItem[]).map((role) => role.id);
    if (memberRoleId !== undefined && !currentIds.includes(memberRoleId)) {
      return uniqueIds([...currentIds, memberRoleId]);
    }
    return uniqueIds(currentIds);
  }, [record, memberRoleId]);

  const handleChangeRoles: SubmitHandler<ChangeRolesFormValues> = (payload) => {
    if (typeof mutate !== 'function') {
      return;
    }

    const selected = uniqueIds(payload.roleIds || []);
    const roleIds = memberRoleId !== undefined && !selected.includes(memberRoleId) ? [...selected, memberRoleId] : selected;

    mutate({variables: {...params, id, payload: {roleIds}}})
      .then((res) => {
        if (res.data?.item) {
          const itemName = res.data.item.name || res.data.item.id || '';
          toast.success(`AllUser ${itemName} has been updated successfully!`);
          if (typeof onSuccess === 'function') {
            onSuccess();
          }
          close();
        }
      })
      .catch((error: {message?: string}) => {
        if (error.message) {
          toast.error(error.message);
        }
      });
  };

  return (
    <FormContainer
      FormProps={{id: CHANGE_ROLES_FORM_ID, noValidate: true}}
      defaultValues={{roleIds: defaultRoleIds}}
      onSuccess={handleChangeRoles}
    >
      <Typography variant="body2" sx={{mb: 1}}>
        Member is always kept. Add or remove extra roles for this user.
      </Typography>
      <AutocompleteInput<RoleOption>
        multiple
        fullWidth
        name="roleIds"
        label="Roles"
        options={options}
        autocompleteProps={{
          size: 'small',
          fullWidth: true,
          getOptionDisabled: (option) => option.name === MEMBER_ROLE_NAME
        }}
      />
    </FormContainer>
  );
};

const ChangeRolesMenuItem = ({roles, ...props}: ActionMenuItemProps & {roles: RoleItem[]}) => {
  const {onSuccess, ...rest} = props;

  return (
    <ActionMenuItemConfirmDialog
      {...rest}
      label="Change roles"
      icon={<ManageAccounts />}
      confirmTitle="Change roles"
      confirmContent={<ChangeRolesForm roles={roles} onSuccess={onSuccess} />}
      primaryButton={
        <Button type="submit" form={CHANGE_ROLES_FORM_ID} variant="contained">
          Save
        </Button>
      }
    />
  );
};

const BlockUnblockMenuItem = (props: {onSuccess?: () => void; onClose?: () => void; variant?: 'cell' | 'standalone'}) => {
  const {record} = useRecordContext();
  if (record?.block) {
    return (
      <ActionMenuItemUpdate
        {...props}
        label="Unblock"
        icon={<Check />}
        data={{block: false}}
        confirmTitle="Unblock user"
        confirmContent="Allow this user to sign in again?"
      />
    );
  }

  return (
    <ActionMenuItemUpdate
      {...props}
      label="Block"
      icon={<Block />}
      data={{block: true}}
      confirmTitle="Block user"
      confirmContent="Blocked users cannot sign in. Continue?"
      sx={{color: 'error.main'}}
    />
  );
};

const AllUsers = () => {
  const {data: rolesData} = useQuery<{items: RoleItem[]}>(getRoles);
  const roles = useMemo(() => rolesData?.items || [], [rolesData]);

  return (
    <ResourceContextProvider resource="AllUser">
      <List refresh listFilters={<AllUsersFilters />} toolbar={<TotalChip label="users" sx={{mb: 1}} />}>
        <ListField source="name" label="User" />
        <ListField source="email" label="Email" />
        <UserGroupsField source="userGroups" label="Groups" />
        <UserRolesField source="roles" label="Roles" />
        <BooleanField source="hasGarmin" label="Garmin">
          <Check />
        </BooleanField>
        <BooleanField source="hasSuunto" label="Suunto">
          <Check />
        </BooleanField>
        <BooleanField source="hasCoros" label="COROS">
          <Check />
        </BooleanField>
        <UserStatusField source="active" label="Status" />
        <ActionMenu variant="cell" label="Actions" align="right" width="5%">
          <BlockUnblockMenuItem />
          <ChangeRolesMenuItem roles={roles} />
          <RecentActivitiesMenuItem />
        </ActionMenu>
      </List>
    </ResourceContextProvider>
  );
};

export default AllUsers;
